import React, { useEffect, useState } from 'react';
import IonicProviderPageShell from 'common/molecules/ionicProviderPageShell/IonicProviderPageShell';
import { useParams, useLocation } from 'react-router';
import { useAuthContext } from 'providers/auth-provider';
import useFetch from 'use-http';
import Header from 'components/Header';
import { WholeMeClient } from '../util/wholeme_client';
import WholeMeProfile from '../pages/profile/wholeme/WholeMeProfile';
import { useAppState } from '../../../providers/app-state/app-state-provider';
import { CoachLubavProfileData } from '../util/coachLubav_Profile';
import {
  useUserPermissionsQuery,
  useProfilePermissionsQuery,
} from '../../../redux/apis/permissions.api';

const WholeMeProfileRoute = () => {
  const params = useParams();
  const searchParams = new URLSearchParams(useLocation().search);
  const initialMode = useLocation().state?.initialMode || searchParams.get('mode') || 'profile';
  const userRole = searchParams.get('role');
  const { user } = useAuthContext();
  const {
    user: { postTargets: targets },
  } = useAppState();

  const { get: doGet } = useFetch();
  const pageLoadedRef = React.useRef(false);
  const [userPublicData, setUserDataRequested] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const userId = params?.userId || params?.id || user?.id;
  const prevUserIdRef = React.useRef(userId);
  const isViewingAdmin = userRole === 'admin'; // this should actually looking at a lubav profile

  const { data: userPermissions, isLoading: loadingUserPermissions } = useUserPermissionsQuery({ user }, { skip: !user });
  const { data: profilePermissions, isLoading: loadingProfilePermissions } = useProfilePermissionsQuery(
    { userId, targetProfile: userPublicData },
    {
      skip: !user || !userPublicData,
      refetchOnMountOrArgChange: true,
    },
  );

  const fetchPublicProfile = async () => {
    const userData = isViewingAdmin
      ? CoachLubavProfileData(userId).publicProfileData
      : await doGet(`/users/profile/${userId}`); // todo: update this endpoint to only return data scoped to user permissions.
    setUserDataRequested(userData);
    return { profile: userData };
  };

  const refreshProfile = async ({ loadingSpinner = true } = {}) => {
    if (loadingSpinner) {
      setIsLoading(true);
    }

    try {
      const refreshedProfile = await fetchPublicProfile();
      setIsLoading(false);
      return refreshedProfile;
    } catch (e) {
      console.error('Error refreshing profile:', e);
    } finally {
      setIsLoading(false);
    }
    return null;
  };

  useEffect(() => {
    void (async () => {
    setIsLoading(true);
    if (prevUserIdRef.current !== userId) {
      pageLoadedRef.current = false;
      prevUserIdRef.current = userId;
    }

    if (userId && !pageLoadedRef.current) {
      await fetchPublicProfile();
      pageLoadedRef.current = true;
      setIsLoading(false);
    }

    return () => {
      pageLoadedRef.current = false;
    };
  })();
  }, [userId]);

  if (!userPublicData) {
    return null;
  }

  const profileIsLoading = loadingUserPermissions || loadingProfilePermissions || isLoading;

  return (
    <IonicProviderPageShell header={<Header />}>
      <WholeMeProfile
        key={userId}
        userId={Number(userId)}
        viewingUser={user}
        wholeMeClient={isViewingAdmin ? new WholeMeClient({ admin: true }) : new WholeMeClient()}
        postTargets={targets}
        legacyProfileData={userPublicData}
        isViewingAdmin={isViewingAdmin}
        isLoading={profileIsLoading}
        setIsLoading={setIsLoading}
        profilePermissions={profilePermissions}
        userPermissions={userPermissions}
        refreshProfile={refreshProfile}
        initialMode={initialMode}
      />
    </IonicProviderPageShell>
  );
};

export default WholeMeProfileRoute;
